/**
 * Accès données du picker du bloc CMS avancé `ItemSpotlight` (mise en avant d'UN
 * élément, toutes tables d'œuvres confondues). Dispatcher unifié par
 * `SelectionItemType` → `{ id, title, registrationNumber?, url? }` pour la grille
 * de sélection et la résolution de l'item choisi (chip). Réutilise les fetchers
 * « tiny » existants quand ils exposent une vignette (œuvres / photos) ; appelle
 * les endpoints de liste standard sinon (attachments inclus). Chaque item porte
 * son numéro d'enregistrement (affiché à côté du titre).
 */
import api from "@/lib/axiosConfig";
import { getTinyArtworks } from "@/api/artwork/artwork";
import { getTinyPhotographies } from "@/api/photography/photography";
import { getDefaultAttachment } from "@/lib/image";
import type { Attachment } from "@/lib/types";
import type { SelectionItemType } from "@/lib/cms-puck/blocks/selection";

export type CatalogPickItem = {
  id: string;
  title: string;
  /** Numéro d'enregistrement (affiché à côté du titre dans le picker). */
  registrationNumber?: string;
  url?: string;
};

const PAGE_SIZE = 60;

/** Clé `defaultPosition` pour choisir la vignette face selon le type catalogue. */
type ThumbAttachmentType =
  | "artwork"
  | "photography"
  | "multipleprint"
  | "multiplesample"
  | "writing"
  | "document"
  | "audiovisual"
  | "sculptureEdition"
  | "tirage"
  | "exhibition"
  | "bibliography";

function catalogThumbType(type: SelectionItemType): ThumbAttachmentType {
  if (type === "sculpture") return "sculptureEdition";
  if (type === "image") return "artwork";
  return type as ThumbAttachmentType;
}

function attachmentThumb(
  attachments: Attachment[] | undefined,
  type: SelectionItemType,
): string | undefined {
  if (!attachments?.length) return undefined;
  const att = getDefaultAttachment(attachments, catalogThumbType(type));
  if (!att) return undefined;
  if (att.kind === "video" || att.kind === "embed") {
    return att.posterPublicUrl ?? att.mediumPublicUrl ?? att.publicUrl;
  }
  return att.mediumPublicUrl ?? att.publicUrl ?? att.largePublicUrl;
}

/** Filtre `contains` insensible sur un champ, ou `{}` si pas de recherche. */
function containsWhere(field: string, search: string): object {
  const s = search.trim();
  return s ? { [field]: { contains: s, mode: "insensitive" } } : {};
}

/** POST liste générique (écrits / documents / audiovisuel / sculptures…). */
async function postList<T>(
  route: string,
  where: object,
  orderByField: string,
  take: number,
): Promise<T[]> {
  const { data } = await api.post<T[]>(`${route}?take=${take}&skip=0`, {
    where,
    orderBy: { [orderByField]: "asc" },
  });
  return data;
}

type WithAttachments = { attachments?: Attachment[] };

/** Charge les attachments pour une liste de docIds (ex. tirages all-tiny sans médias). */
async function fetchAttachmentsByDocIds(
  docIds: string[],
): Promise<Map<string, Attachment[]>> {
  const map = new Map<string, Attachment[]>();
  if (docIds.length === 0) return map;
  const { data } = await api.post<Attachment[]>("back/api/attachment", {
    where: { docId: { in: docIds } },
    orderBy: { position: "asc" },
  });
  for (const att of data ?? []) {
    const list = map.get(att.docId) ?? [];
    list.push(att);
    map.set(att.docId, list);
  }
  return map;
}

type RawTirageTiny = {
  id: string;
  tirageDescriptionNumeroEnregistrement: string;
  artwork?: { oeuvresDescriptionTitre?: string } | null;
};

/** Liste filtrée d'un type → items `{ id, title, registrationNumber, url? }`. */
export async function fetchCatalogList(
  type: SelectionItemType,
  search: string,
  take = PAGE_SIZE,
): Promise<CatalogPickItem[]> {
  switch (type) {
    case "artwork": {
      const rows = await getTinyArtworks({
        where: containsWhere("oeuvresDescriptionTitre", search),
        orderBy: { oeuvresDescriptionTitre: "asc" },
        take,
      });
      return rows.map((a) => ({
        id: a.id,
        title: a.oeuvresDescriptionTitre || "",
        registrationNumber: a.oeuvresDescriptionNumeroEnregistrement,
        url: attachmentThumb(a.attachments, "artwork"),
      }));
    }
    case "photography": {
      const rows = await getTinyPhotographies({
        where: containsWhere("photosDescriptionTitre", search),
        orderBy: { photosDescriptionTitre: "asc" },
        take,
      });
      return rows.map((p) => ({
        id: p.id,
        title: p.photosDescriptionTitre || "",
        registrationNumber: p.photosDescriptionNumeroEnregistrement,
        url: attachmentThumb(
          (p as WithAttachments).attachments,
          "photography",
        ),
      }));
    }
    case "multipleprint": {
      const rows = await postList<MultipleprintRow>(
        "back/api/multipleprint",
        containsWhere("estampesMultiplesDescriptionTitre", search),
        "estampesMultiplesDescriptionTitre",
        take,
      );
      return rows.map(mapMultipleprint);
    }
    case "writing": {
      const rows = await postList<WritingRow>(
        "back/api/writings",
        containsWhere("ecritsDescriptionTitre", search),
        "ecritsDescriptionTitre",
        take,
      );
      return rows.map(mapWriting);
    }
    case "document": {
      const rows = await postList<DocumentRow>(
        "back/api/documents",
        containsWhere("documentsDescriptionTitre", search),
        "documentsDescriptionTitre",
        take,
      );
      return rows.map(mapDocument);
    }
    case "audiovisual": {
      const rows = await postList<AudiovisualRow>(
        "back/api/audiovisuals",
        containsWhere("audiovisuelDescriptionTitre", search),
        "audiovisuelDescriptionTitre",
        take,
      );
      return rows.map(mapAudiovisual);
    }
    case "sculpture": {
      const rows = await postList<SculptureRow>(
        "back/api/sculpture-edition",
        containsWhere("sculptureEditionNumeroEnregistrement", search),
        "sculptureEditionNumeroEnregistrement",
        take,
      );
      return rows.map(mapSculpture);
    }
    case "tirage": {
      const { data } = await api.post<RawTirageTiny[]>(
        `back/api/tirages/all-tiny?take=${take}&skip=0`,
        { where: containsWhere("tirageDescriptionNumeroEnregistrement", search) },
      );
      const rows = data ?? [];
      const byDoc = await fetchAttachmentsByDocIds(rows.map((r) => r.id));
      return rows.map((t) => ({
        ...mapTirage(t),
        url: attachmentThumb(byDoc.get(t.id), "tirage"),
      }));
    }
    case "multiplesample": {
      const rows = await postList<MultiplesampleRow>(
        "back/api/multiplesample",
        containsWhere(
          "exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement",
          search,
        ),
        "exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement",
        take,
      );
      return rows.map(mapMultiplesample);
    }
    case "exhibition": {
      const rows = await postList<ExhibitionRow>(
        "back/api/exhibitions",
        containsWhere("expositionsDescriptionTitre", search),
        "expositionsDescriptionTitre",
        take,
      );
      return rows.map(mapExhibition);
    }
    case "bibliography": {
      const rows = await postList<BibliographyRow>(
        "back/api/bibliography",
        containsWhere("bibliographieDescriptionTitre", search),
        "bibliographieDescriptionTitre",
        take,
      );
      return rows.map(mapBibliography);
    }
    default:
      return [];
  }
}

/** Résout les items sélectionnés (par ids) d'un type, pour l'aperçu du chip. */
export async function fetchCatalogItemsByIds(
  type: SelectionItemType,
  ids: string[],
): Promise<CatalogPickItem[]> {
  if (ids.length === 0) return [];
  const where = { id: { in: ids } };
  switch (type) {
    case "artwork": {
      const rows = await getTinyArtworks({ where, take: ids.length });
      return rows.map((a) => ({
        id: a.id,
        title: a.oeuvresDescriptionTitre || "",
        registrationNumber: a.oeuvresDescriptionNumeroEnregistrement,
        url: attachmentThumb(a.attachments, "artwork"),
      }));
    }
    case "photography": {
      const rows = await getTinyPhotographies({ where, take: ids.length });
      return rows.map((p) => ({
        id: p.id,
        title: p.photosDescriptionTitre || "",
        registrationNumber: p.photosDescriptionNumeroEnregistrement,
        url: attachmentThumb(
          (p as WithAttachments).attachments,
          "photography",
        ),
      }));
    }
    case "multipleprint": {
      const rows = await postList<MultipleprintRow>(
        "back/api/multipleprint",
        where,
        "estampesMultiplesDescriptionTitre",
        ids.length,
      );
      return rows.map(mapMultipleprint);
    }
    case "writing": {
      const rows = await postList<WritingRow>(
        "back/api/writings",
        where,
        "ecritsDescriptionTitre",
        ids.length,
      );
      return rows.map(mapWriting);
    }
    case "document": {
      const rows = await postList<DocumentRow>(
        "back/api/documents",
        where,
        "documentsDescriptionTitre",
        ids.length,
      );
      return rows.map(mapDocument);
    }
    case "audiovisual": {
      const rows = await postList<AudiovisualRow>(
        "back/api/audiovisuals",
        where,
        "audiovisuelDescriptionTitre",
        ids.length,
      );
      return rows.map(mapAudiovisual);
    }
    case "sculpture": {
      const rows = await postList<SculptureRow>(
        "back/api/sculpture-edition",
        where,
        "sculptureEditionNumeroEnregistrement",
        ids.length,
      );
      return rows.map(mapSculpture);
    }
    case "tirage": {
      const { data } = await api.post<RawTirageTiny[]>(
        `back/api/tirages/all-tiny?take=${ids.length}&skip=0`,
        { where },
      );
      const rows = data ?? [];
      const byDoc = await fetchAttachmentsByDocIds(rows.map((r) => r.id));
      return rows.map((t) => ({
        ...mapTirage(t),
        url: attachmentThumb(byDoc.get(t.id), "tirage"),
      }));
    }
    case "multiplesample": {
      const rows = await postList<MultiplesampleRow>(
        "back/api/multiplesample",
        where,
        "exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement",
        ids.length,
      );
      return rows.map(mapMultiplesample);
    }
    case "exhibition": {
      const rows = await postList<ExhibitionRow>(
        "back/api/exhibitions",
        where,
        "expositionsDescriptionTitre",
        ids.length,
      );
      return rows.map(mapExhibition);
    }
    case "bibliography": {
      const rows = await postList<BibliographyRow>(
        "back/api/bibliography",
        where,
        "bibliographieDescriptionTitre",
        ids.length,
      );
      return rows.map(mapBibliography);
    }
    default:
      return [];
  }
}

// --- Lignes brutes + mappers par type (id + titre + n° + vignette) ------------

type MultipleprintRow = {
  id: string;
  estampesMultiplesDescriptionTitre: string;
  estampesMultiplesDescriptionNumeroEnregistrement: string;
} & WithAttachments;
const mapMultipleprint = (m: MultipleprintRow): CatalogPickItem => ({
  id: m.id,
  title: m.estampesMultiplesDescriptionTitre || "",
  registrationNumber: m.estampesMultiplesDescriptionNumeroEnregistrement,
  url: attachmentThumb(m.attachments, "multipleprint"),
});

type WritingRow = {
  id: string;
  ecritsDescriptionTitre: string;
  ecritsDescriptionNumeroEnregistrement: string;
} & WithAttachments;
const mapWriting = (w: WritingRow): CatalogPickItem => ({
  id: w.id,
  title: w.ecritsDescriptionTitre || "",
  registrationNumber: w.ecritsDescriptionNumeroEnregistrement,
  url: attachmentThumb(w.attachments, "writing"),
});

type DocumentRow = {
  id: string;
  documentsDescriptionTitre: string;
  documentsDescriptionNumeroEnregistrement: string;
} & WithAttachments;
const mapDocument = (d: DocumentRow): CatalogPickItem => ({
  id: d.id,
  title: d.documentsDescriptionTitre || "",
  registrationNumber: d.documentsDescriptionNumeroEnregistrement,
  url: attachmentThumb(d.attachments, "document"),
});

type AudiovisualRow = {
  id: string;
  audiovisuelDescriptionTitre: string;
  audiovisuelDescriptionNumeroEnregistrement: string;
} & WithAttachments;
const mapAudiovisual = (a: AudiovisualRow): CatalogPickItem => ({
  id: a.id,
  title: a.audiovisuelDescriptionTitre || "",
  registrationNumber: a.audiovisuelDescriptionNumeroEnregistrement,
  url: attachmentThumb(a.attachments, "audiovisual"),
});

type SculptureRow = {
  id: string;
  sculptureEditionNumeroEnregistrement: string;
  masterArtwork?: { oeuvresDescriptionTitre?: string } | null;
} & WithAttachments;
const mapSculpture = (s: SculptureRow): CatalogPickItem => ({
  id: s.id,
  title: s.masterArtwork?.oeuvresDescriptionTitre || "",
  registrationNumber: s.sculptureEditionNumeroEnregistrement,
  url: attachmentThumb(s.attachments, "sculpture"),
});

const mapTirage = (t: RawTirageTiny): CatalogPickItem => ({
  id: t.id,
  title: t.artwork?.oeuvresDescriptionTitre || "",
  registrationNumber: t.tirageDescriptionNumeroEnregistrement,
});

type MultiplesampleRow = {
  id: string;
  exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement: string;
  multipleprints?: Array<{ estampesMultiplesDescriptionTitre?: string }>;
} & WithAttachments;
const mapMultiplesample = (m: MultiplesampleRow): CatalogPickItem => ({
  id: m.id,
  title: m.multipleprints?.[0]?.estampesMultiplesDescriptionTitre || "",
  registrationNumber:
    m.exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement,
  url: attachmentThumb(m.attachments, "multiplesample"),
});

type ExhibitionRow = {
  id: string;
  expositionsDescriptionTitre: string;
  expositionsDescriptionNumeroExpo: string;
} & WithAttachments;
const mapExhibition = (e: ExhibitionRow): CatalogPickItem => ({
  id: e.id,
  title: e.expositionsDescriptionTitre || "",
  registrationNumber: e.expositionsDescriptionNumeroExpo,
  url: attachmentThumb(e.attachments, "exhibition"),
});

type BibliographyRow = {
  id: string;
  bibliographieDescriptionTitre: string;
  bibliographieDescriptionNumeroBibliographie: string;
} & WithAttachments;
const mapBibliography = (b: BibliographyRow): CatalogPickItem => ({
  id: b.id,
  title: b.bibliographieDescriptionTitre || "",
  registrationNumber: b.bibliographieDescriptionNumeroBibliographie,
  url: attachmentThumb(b.attachments, "bibliography"),
});
